import type { User } from "@auth0/auth0-react";

export interface AuthFlowResult {
	shouldRedirect: boolean;
	redirectPath?: string;
	reason?:
		| "2fa_required"
		| "onboarding_required"
		| "flow_complete"
		| "claims_missing";
}

/**
 * Analyzes JWT claims to determine where the user should be redirected
 * This is the single source of truth for post-authentication flow logic
 */
export function analyzeAuthFlow(
	user: User | undefined,
	currentPath: string,
	defaultDestination = "/",
): AuthFlowResult {
	if (!user) {
		return {
			shouldRedirect: false,
		};
	}

	// Check if JWT claims are present (set by Auth0 post-login action)
	// On post-registration, post-login action is NOT triggered, so claims are undefined
	const claimsPresent =
		user["https://eraborn.com/needs_2fa"] !== undefined ||
		user["https://eraborn.com/needs_onboarding"] !== undefined;

	if (!claimsPresent) {
		// Claims missing - caller should fetch status from API
		// This happens on mobile post-registration when post-login action wasn't triggered
		console.log(
			"⚠️ [AUTH_FLOW] JWT claims missing (post-registration), need to fetch status from API",
		);
		return {
			shouldRedirect: false,
			reason: "claims_missing",
		};
	}

	// 1. Check if 2FA is required (highest priority)
	const needs2FA = user["https://eraborn.com/needs_2fa"];
	if (needs2FA) {
		// Don't redirect if already on 2FA page
		if (currentPath === "/2fa") {
			return { shouldRedirect: false };
		}

		console.log("🔐 [AUTH_FLOW] 2FA required, redirecting to 2FA page");
		return {
			shouldRedirect: true,
			redirectPath: `/2fa?callbackUrl=${encodeURIComponent(defaultDestination)}`,
			reason: "2fa_required",
		};
	}

	// 2. Check onboarding status (second priority)
	const needsOnboarding = user["https://eraborn.com/needs_onboarding"];
	if (needsOnboarding) {
		// Don't redirect if already on onboarding page
		if (currentPath === "/onboarding") {
			return { shouldRedirect: false };
		}

		console.log(
			"📝 [AUTH_FLOW] Onboarding required, redirecting to onboarding",
		);
		return {
			shouldRedirect: true,
			redirectPath: "/onboarding",
			reason: "onboarding_required",
		};
	}

	// 3. All checks passed - user can access their intended destination
	// If user is on callback page, redirect to final destination
	if (currentPath === "/callback") {
		return {
			shouldRedirect: true,
			redirectPath: defaultDestination,
			reason: "flow_complete",
		};
	}

	// User is already where they should be
	return { shouldRedirect: false };
}
